import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const WHITE_LOGO = "/images/logos/logo-white.png";
const BLACK_LOGO = "/images/logos/logo-black.png";

const TermsOfService = () => {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-white text-gray-900'}`}>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header with Logo */}
        <header className="mb-12">
          <Link to="/" className="inline-block mb-8">
            <img src={isDark ? WHITE_LOGO : BLACK_LOGO} alt="BoxCrowd" className="h-10 w-auto" />
          </Link>
          <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Last Updated: April 2025</p>
        </header>

        {/* Content */}
        <div className={`space-y-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Agreement to Terms</h2>
            <p className="leading-relaxed">
              By joining the BoxCrowd waitlist, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not submit your email address.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Waitlist Registration</h2>
            <p className="leading-relaxed mb-3">
              By providing your email address:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You confirm that you are at least 18 years of age</li>
              <li>You provide accurate and complete information</li>
              <li>You consent to receive emails from BoxCrowd regarding our launch and updates</li>
              <li>You understand that waitlist registration does not guarantee early access or any specific benefits</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Communications</h2>
            <p className="leading-relaxed">
              By joining our waitlist, you agree to receive periodic email communications from BoxCrowd, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
              <li>Platform launch notifications</li>
              <li>Product updates and feature announcements</li>
              <li>Important service information</li>
            </ul>
            <p className="leading-relaxed mt-3">
              You may unsubscribe from these communications at any time by clicking the unsubscribe link in any email or contacting us at <a href="mailto:contact@boxcrowd.com" className="text-[#00D4AA] hover:underline">contact@boxcrowd.com</a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Intellectual Property</h2>
            <p className="leading-relaxed">
              All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of BoxCrowd and is protected by copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Limitation of Liability</h2>
            <p className="leading-relaxed">
              BoxCrowd is currently in development. The information provided on this holding page is subject to change. We make no warranties or representations about the accuracy or completeness of the content on this site.
            </p>
            <p className="leading-relaxed mt-3">
              To the fullest extent permitted by law, BoxCrowd shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of this website or waitlist registration.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Privacy</h2>
            <p className="leading-relaxed">
              Your use of this website and submission of your email address is also governed by our <Link to="/privacy" className="text-[#00D4AA] hover:underline">Privacy Policy</Link>. Please review our Privacy Policy to understand how we collect, use, and protect your information.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>No Partnership or Agency</h2>
            <p className="leading-relaxed">
              Joining our waitlist does not create any partnership, joint venture, employment, or agency relationship between you and BoxCrowd.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Changes to Terms</h2>
            <p className="leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to this page with an updated "Last Updated" date. Your continued participation in the waitlist after any changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Governing Law</h2>
            <p className="leading-relaxed">
              These Terms of Service shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contact Information</h2>
            <p className="leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-3">
              <a href="mailto:contact@boxcrowd.com" className="text-[#00D4AA] hover:underline font-semibold">contact@boxcrowd.com</a>
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Severability</h2>
            <p className="leading-relaxed">
              If any provision of these Terms of Service is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms of Service will otherwise remain in full force and effect.
            </p>
          </section>
        </div>

        {/* Back to Home Link */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
          <Link to="/" className="text-[#00D4AA] hover:underline inline-flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
